import { useCallback, useEffect, useMemo, useState } from 'react';
import { axialHexDistance, type HexTileMapKey } from '@/game/hexmap';
import { type BuildingType, buildingLevel, watchStats } from '@/game/sim';
import { cityName } from '@/game/view/cities';
import type { Frame, FrameStore } from '@/game/view/frame';
import { cityTiles, foundableTiles, placeableTiles } from '@/game/view/placement';
import { buildingCityLevel, type PlacementRefusal, placeRejectionText } from '@/game/view/reasons';
import { stockInUnits } from '@/game/view/resources';
import { fighterIds } from '@/game/view/stance';
import { type TargetMode, type UiStore, useUi } from '../ui/store';

export interface TargetBanner {
  text: string;
  /** The text is why the last click was refused. */
  refused: boolean;
}

/**
 * Placement mode (a building placed from a city, in that city's territory;
 * no unit needed: auto workers build placed sites) and tile targeting (a
 * watchtower's watch spot, a settler's founding spot): entering and leaving
 * them, the tiles they tint (from what the player knows, fog-safe), why the
 * last click was refused, and ending them when what they're for goes away.
 */
export function useMatchModes(ui: UiStore, frames: FrameStore, frame: Frame) {
  const placementMode = useUi(ui, (s) => s.placementMode);
  const placementCityId = useUi(ui, (s) => s.placementCityId);
  const targetMode = useUi(ui, (s) => s.targetMode);
  const selectedUnitIds = useUi(ui, (s) => s.selectedUnitIds);
  const selectedBuildingId = useUi(ui, (s) => s.selectedBuildingId);
  const { view, playerId, rules } = frame;

  // Why the last placement click was refused (for that tile), for the HUD banner.
  const [placementError, setPlacementError] = useState<PlacementRefusal | null>(null);
  const enterPlacement = useCallback(
    (type: BuildingType, cityId: string) => {
      ui.set({ targetMode: null, placementMode: type, placementCityId: cityId });
      setPlacementError(null);
    },
    [ui]
  );
  const cancelPlacement = useCallback(() => ui.set({ placementMode: null }), [ui]);

  // Why the last targeting click was refused (for the banner).
  const [targetError, setTargetError] = useState<string | null>(null);
  const enterTargeting = useCallback(
    (mode: TargetMode) => {
      ui.set({ placementMode: null, targetMode: mode });
      setTargetError(null);
    },
    [ui]
  );
  const cancelTargeting = useCallback(() => ui.set({ targetMode: null }), [ui]);

  // Placement ends if the local player is eliminated mid-placement (the HUD
  // hides the banner then, and a hidden sticky mode would silently swallow
  // clicks for a spectating player), or when the city it was started from falls.
  useEffect(() => {
    if (
      placementMode &&
      ((frame.view.players[frame.playerId]?.eliminated ?? false) ||
        !frame.view.buildings[placementCityId ?? ''])
    ) {
      ui.set({ placementMode: null });
    }
  }, [frame, placementMode, placementCityId, ui]);

  // Tiles the armed building can go on, from what the player knows.
  const placeable = useMemo(() => {
    if (!placementMode) return null;
    const { view: state, ctx, fog } = frame;
    const city = state.buildings[placementCityId ?? ''];
    return placeableTiles(
      state,
      ctx,
      frame.playerId,
      placementMode,
      (key) => frame.knownBuilding(key) !== null,
      (key) => fog.explored.has(key),
      city ? cityTiles(state, ctx.rules, city.id) : new Set()
    );
  }, [frame, placementMode, placementCityId]);
  // A refusal goes once its tile becomes placeable (stock arrived, building
  // gone), so the banner never contradicts the tint and hover outline.
  useEffect(() => {
    if (placementError?.key && placeable?.has(placementError.key as HexTileMapKey)) {
      setPlacementError(null);
    }
  }, [placeable, placementError]);

  // Cities chip row while placing: placement moves to that city's territory;
  // below the building's era, say so now rather than on a refused click.
  const placeFromCity = useCallback(
    (id: string) => {
      const placing = ui.get().placementMode;
      const { view: state, rules, playerId, cityNumbers, playerName, names } = frames.get();
      const city = state.buildings[id];
      if (!placing || !city || city.buildProgress < 1) return;
      ui.set({ placementCityId: id });
      setPlacementError(
        buildingLevel(city) < buildingCityLevel(rules, placing)
          ? {
              key: null,
              text: placeRejectionText(rules, placing, 'era', stockInUnits(state, playerId), {
                city: cityName(city, playerId, cityNumbers, playerName, names),
              }),
            }
          : null
      );
    },
    [frames, ui]
  );

  // A lone own settler selected (its "Found city at…" mode ends when it's
  // deselected). While picking its spot, tint where it could found a city.
  const lone = selectedUnitIds.length === 1 ? view.units[selectedUnitIds[0]] : undefined;
  const lonelySettlerId =
    lone?.owner === playerId && rules.units[lone.type]?.actions.includes('found-city')
      ? lone.id
      : null;
  // Only while picking its founding spot: once a tile is picked, the
  // pending-founding ring (MoveTargetMarker) is all that stays.
  const foundingUnitId = targetMode?.kind === 'found' ? targetMode.unitId : null;
  const foundable = useMemo(() => {
    if (!foundingUnitId || placementMode) return null;
    const unit = frame.view.units[foundingUnitId];
    if (!unit) return null;
    return foundableTiles(frame.ctx, unit.type, frame.fog.explored, frame.knownBuilding);
  }, [frame, foundingUnitId, placementMode]);

  // Watch-spot targeting: the tiles within the tower's reach (any tile on the
  // map, fog included: watching is how the fog is looked into).
  const tower = targetMode?.kind === 'watch' ? view.buildings[targetMode.buildingId] : undefined;
  const watchRange = tower ? watchStats(rules, tower)?.range : undefined;
  const towerQ = tower?.pos.q;
  const towerR = tower?.pos.r;
  const { terrain } = frame;
  const watchable = useMemo(() => {
    if (towerQ === undefined || towerR === undefined || watchRange === undefined) return null;
    const result = new Set<HexTileMapKey>();
    for (const [key, tile] of terrain) {
      if (axialHexDistance(tile.coordinates, { q: towerQ, r: towerR }) <= watchRange) {
        result.add(key);
      }
    }
    return result;
  }, [terrain, towerQ, towerR, watchRange]);
  // The tinted tiles while targeting: the tower's reach, or the settler's foundable tiles.
  const targetable = targetMode?.kind === 'watch' ? watchable : foundable;

  // Targeting ends when what it's for goes away: the tower is deselected or
  // falls, the settler is deselected or spent, the attack-move's fighters
  // are deselected or dead.
  const hasFighters = fighterIds(view, rules, playerId, selectedUnitIds).length > 0;
  const towerStands = !!tower;
  useEffect(() => {
    if (!targetMode) return;
    const stillThere =
      targetMode.kind === 'watch'
        ? selectedBuildingId === targetMode.buildingId && towerStands
        : targetMode.kind === 'attackMove'
          ? hasFighters
          : lonelySettlerId === targetMode.unitId;
    if (!stillThere) ui.set({ targetMode: null });
  }, [targetMode, selectedBuildingId, towerStands, lonelySettlerId, hasFighters, ui]);
  // A refusal goes once the cursor moves to a tile that would do.
  useEffect(() => {
    if (!targetError || !targetable) return;
    const check = () => {
      const key = ui.get().hoveredKey;
      if (key && targetable.has(key)) setTargetError(null);
    };
    check();
    return ui.subscribe(check);
  }, [targetError, targetable, ui]);

  // The targeting banner: the last refusal, else what to do.
  const targetBanner: TargetBanner | null = !targetMode
    ? null
    : targetError
      ? { text: targetError, refused: true }
      : {
          text:
            targetMode.kind === 'watch'
              ? `Click a tile up to ${watchRange ?? 0} tiles from the tower to watch it — Esc/right-click cancels`
              : targetMode.kind === 'attackMove'
                ? 'Click a tile: aggressive fighters fight what they meet, held or passive ones just walk — Esc/right-click cancels'
                : 'Click a tinted tile: the settler walks there and founds a city — Esc/right-click cancels',
          refused: false,
        };

  return {
    placementMode,
    placementCityId,
    targetMode,
    placementError,
    setPlacementError,
    setTargetError,
    enterPlacement,
    cancelPlacement,
    enterTargeting,
    cancelTargeting,
    placeFromCity,
    placeable,
    foundable,
    watchable,
    targetable,
    targetBanner,
  };
}

export type MatchModes = ReturnType<typeof useMatchModes>;
